import type { StandardServicePageContent } from "~/content/servicePage";

export const GasInstallationAcceptanceContent: StandardServicePageContent = {
  heroTitle: "Odbiór instalacji gazowej",
  quickFactsTitle: "Najważniejsze informacje w skrócie",
  quickFacts: [
    {
      label: "Kiedy potrzebny",
      value: "Po nowej instalacji albo istotnej przebudowie",
      description:
        "To etap poprzedzający przekazanie instalacji do użytkowania, zakończenie budowy albo dalsze formalności związane z uruchomieniem gazu w obiekcie.",
    },
    {
      label: "Co jest sprawdzane",
      value: "Zgodność wykonania, próba szczelności, armatura i dokumenty",
      description:
        "Odbiór nie sprowadza się do jednego podpisu. Liczy się stan instalacji, kompletność robót, wyniki prób oraz gotowość dokumentacji do zamknięcia procesu budowlanego.",
    },
    {
      label: "Jakie dokumenty",
      value: "Projekt, oświadczenia i protokół próby szczelności",
      description:
        "W zależności od inwestycji potrzebne mogą być też inne protokoły, dziennik budowy i dokumentacja powykonawcza. Braki formalne często opóźniają zakończenie inwestycji bardziej niż same poprawki techniczne.",
    },
    {
      label: "Ile kosztuje",
      value: "Cena zależy od wielkości instalacji i zakresu odbioru",
      description:
        "Dom jednorodzinny wycenia się inaczej niż lokal usługowy lub większy budynek. Znaczenie ma też to, czy usługa obejmuje analizę dokumentacji i dodatkowe badania.",
    },
  ],
  faqTitle: "Najczęstsze pytania o odbiór instalacji gazowej",
  sections: [
    {
      title: "Na czym polega odbiór instalacji gazowej",
      paragraphs: [
        `Odbiór instalacji gazowej to końcowa kontrola wykonanych prac przed przekazaniem instalacji do użytkowania albo przed przejściem do kolejnego etapu formalnego związanego z zakończeniem budowy i uruchomieniem gazu. Celem odbioru jest potwierdzenie, że instalacja została wykonana zgodnie z dokumentacją, zasadami wiedzy technicznej i warunkami bezpiecznej eksploatacji.`,
        `W praktyce odbiór nie sprowadza się do jednego podpisu pod gotową instalacją. To moment, w którym sprawdza się spójność wykonania z projektem, kompletność podłączeń, wyniki prób i badań oraz przygotowanie dokumentacji potrzebnej inwestorowi, kierownikowi budowy albo organowi nadzoru budowlanego.`,
        `Dobrze przeprowadzony odbiór ogranicza ryzyko sporów z wykonawcą, opóźnień przy zakończeniu budowy i sytuacji, w której usterki wychodzą dopiero po uruchomieniu instalacji. Właściciel albo inwestor dostaje jasną informację, czy instalacja może zostać przekazana dalej bezpiecznie i formalnie.`,
      ],
    },
    {
      title: "Kiedy wykonuje się odbiór instalacji gazowej i czy jest obowiązkowy",
      paragraphs: [
        `Odbiór instalacji gazowej wykonuje się po zakończeniu robót związanych z wykonaniem nowej instalacji albo istotną przebudową istniejącego układu. To etap poprzedzający przekazanie instalacji do użytkowania, zakończenie budowy albo dalsze formalności związane z uruchomieniem obiektu.`,
        `W praktyce odbiór jest potrzebny wtedy, gdy inwestor chce potwierdzić, że roboty zostały wykonane prawidłowo i że dokumentacja jest przygotowana do zamknięcia procesu budowlanego. Szczególnie ważny staje się przy nowych domach, lokalach usługowych, budynkach wielorodzinnych i obiektach, w których instalacja gazowa jest częścią większej inwestycji.`,
        `Nie warto odkładać tego etapu na sam koniec bez wcześniejszego przygotowania dokumentów i prób. Jeśli na odbiorze wyjdą braki, konieczne mogą być poprawki, uzupełnienie protokołów albo ponowne badania, co od razu wpływa na termin zakończenia inwestycji.`,
      ],
    },
    {
      title: "Co obejmuje kontrola instalacji gazowej przy odbiorze",
      paragraphs: [
        `Kontrola instalacji gazowej przy odbiorze obejmuje przede wszystkim ocenę zgodności wykonania z projektem lub zakresem robót, stan przewodów, armatury, zaworów, podłączeń i dostępności elementów odcinających. Sprawdza się też, czy instalacja jest kompletna i czy wykonanie nie odbiega od przyjętych rozwiązań technicznych.`,
        `Jednym z kluczowych punktów odbioru jest weryfikacja wykonanych badań, w tym próby szczelności instalacji gazowej. To właśnie ona potwierdza, że układ nie ma nieszczelności i może zostać bezpiecznie przekazany do dalszego użytkowania albo procedury uruchomienia.`,
        `W zależności od obiektu istotne są również warunki wentylacji, sposób podłączenia urządzeń gazowych, przygotowanie przewodów spalinowych oraz kompletność dokumentacji powykonawczej. Odbiór ma odpowiedzieć na konkretne pytanie: czy instalacja została wykonana poprawnie i czy można ją formalnie zamknąć bez ukrytych braków.`,
      ],
    },
    {
      title: "Dokumenty potrzebne do odbioru instalacji gazowej i cena usługi",
      paragraphs: [
        `Zakres dokumentów zależy od rodzaju inwestycji i trybu zakończenia budowy, ale w praktyce najczęściej potrzebne są: projekt techniczny z ewentualnymi zmianami, oświadczenie kierownika budowy o zgodności wykonania z projektem i przepisami, protokół badania szczelności instalacji gazowej oraz inne protokoły badań i sprawdzeń wymagane dla danego obiektu.`,
        `Przy większych inwestycjach dochodzą także dziennik budowy, dokumentacja geodezyjna powykonawcza, potwierdzenie odbioru przyłączy oraz dodatkowe uzgodnienia związane z zakończeniem budowy lub pozwoleniem na użytkowanie. To właśnie braki formalne najczęściej opóźniają domknięcie inwestycji.`,
        `Cena odbioru zależy od wielkości i złożoności instalacji, rodzaju obiektu oraz zakresu czynności objętych wizytą. Inaczej wycenia się prostą instalację w domu jednorodzinnym, a inaczej odbiór w lokalu usługowym albo większym budynku, zwłaszcza jeśli usługa obejmuje analizę dokumentacji, próbę szczelności albo powtórną wizytę po poprawkach.`,
      ],
    },
  ],
  scopeTitle: "Zakres usług odbioru instalacji gazowej",
  scopeItems: [
    "Ocena zgodności z projektem",
    "Kontrola przewodów i armatury",
    "Weryfikacja zaworów odcinających",
    "Sprawdzenie podłączeń urządzeń",
    "Weryfikacja próby szczelności",
    "Kontrola dokumentacji powykonawczej",
    "Ocena gotowości do uruchomienia",
    "Protokół odbiorowy",
  ],
  seo: {
    title: "Odbiór instalacji gazowej: dokumenty, próba szczelności i cena",
    description:
      "Zamów odbiór instalacji gazowej po wykonaniu nowej instalacji lub modernizacji. Sprawdź, jakie dokumenty są potrzebne, co obejmuje kontrola i co wpływa na cenę usługi.",
  },
};
